import { useCallback, useEffect, useState } from 'react'

export const FEATURE_FLAGS_STORAGE_KEY = 'feature-flags'
export const ENABLE_FEATURE_FLAGS_PARAM = 'dev-enable-feature'
export const DISABLE_FEATURE_FLAGS_PARAM = 'dev-disable-feature'

export const FEATURE_FLAGS = [
  'json-editor',
  'log-http-requests',
  'processmodels'
] as const

/**
 * Hook to manage feature flags state.
 * @param featureKey - The key identifier for the feature
 * @returns [boolean, (enable: boolean) => void] - Current feature state and function to update it
 */
export function useFeatureFlag(
  featureKey: (typeof FEATURE_FLAGS)[number]
): [boolean, (enable: boolean) => void] {
  const [isEnabled, setIsEnabled] = useState<boolean>(() => {
    // Initialize from localStorage
    try {
      const flags = JSON.parse(
        localStorage.getItem(FEATURE_FLAGS_STORAGE_KEY) || '{}'
      )
      return !!flags[featureKey]
    } catch {
      return false
    }
  })

  // Update localStorage when feature state changes
  const updateFeatureState = useCallback(
    (enable: boolean) => {
      try {
        const flags = JSON.parse(
          localStorage.getItem(FEATURE_FLAGS_STORAGE_KEY) || '{}'
        )
        flags[featureKey] = enable
        localStorage.setItem(FEATURE_FLAGS_STORAGE_KEY, JSON.stringify(flags))
        setIsEnabled(enable)
      } catch (error) {
        console.error('Error updating feature flag:', error)
      }
    },
    [featureKey]
  )

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const enableParam = urlParams.get(ENABLE_FEATURE_FLAGS_PARAM) === featureKey
    const disableParam =
      urlParams.get(DISABLE_FEATURE_FLAGS_PARAM) === featureKey

    if (enableParam || disableParam) {
      updateFeatureState(enableParam || !disableParam)
    }
  }, [featureKey, updateFeatureState])

  return [isEnabled, updateFeatureState]
}
